/**
 * CategorySpendBreakdown — top spending categories for a selected
 * window (default = last month) using ``/banking/aggregates/monthly``.
 *
 * Renders a horizontal bar chart per category with the percentage of
 * the total month's outflow + the absolute EUR amount. Skips inflow
 * rows because the user reads this as "dove ho speso".
 *
 * Empty state: when the BE returns no aggregates (sandbox / fresh
 * connection) we fall back to a hint instead of a void panel.
 */

import * as React from "react";
import { useLingui } from "@lingui/react/macro";

import type { MonthlyAggregateRow } from "@ando/core";

import { cn } from "@ando/ui";


function formatEur(cents: number, currency: string): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Math.abs(cents) / 100);
}


/** Italian title-case helper for category slugs ("food_delivery" → "Food delivery"). */
function prettyCategory(slug: string | null, uncategorizedLabel: string): string {
  if (!slug) return uncategorizedLabel;
  return slug.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase());
}


export interface CategorySpendBreakdownProps {
  readonly rows: readonly MonthlyAggregateRow[];
  readonly currency?: string;
  /** Max rows rendered (default 6). */
  readonly maxRows?: number;
}


export const CategorySpendBreakdown: React.FC<CategorySpendBreakdownProps> = ({
  rows,
  currency = "EUR",
  maxRows = 6,
}) => {
  const { t } = useLingui();
  // Only outflows for "dove ho speso". Sum into a {category, abs}
  // bucket so we don't render duplicate categories across months.
  const buckets = React.useMemo(() => {
    const map = new Map<string, { readonly category: string; readonly sumCents: number }>();
    for (const r of rows) {
      if (!Number.isFinite(r.sum_cents) || r.sum_cents >= 0) continue;
      const key = r.category ?? "uncategorized";
      const prev = map.get(key);
      const abs = Math.abs(r.sum_cents);
      if (prev) {
        map.set(key, { category: key, sumCents: prev.sumCents + abs });
      } else {
        map.set(key, { category: key, sumCents: abs });
      }
    }
    return Array.from(map.values())
      .sort((a, b) => b.sumCents - a.sumCents)
      .slice(0, maxRows);
  }, [rows, maxRows]);

  if (buckets.length === 0) {
    return (
      <p className="text-[11.5px] text-ink-3">
        {t`Nessuna spesa nel periodo. Le categorie compaiono quando arrivano i primi movimenti di uscita.`}
      </p>
    );
  }

  const total = buckets.reduce((acc, b) => acc + b.sumCents, 0);

  return (
    <ul className="flex flex-col gap-2" aria-label={t`Spese per categoria`}>
      {buckets.map((b) => {
        const pct = total > 0 ? (b.sumCents / total) * 100 : 0;
        return (
          <li key={b.category} className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-3 text-[12px]">
              <span className="truncate text-ink-1">{prettyCategory(b.category, t`Non classificato`)}</span>
              <span className="shrink-0 font-medium text-ink-2 tabular-nums">
                {formatEur(b.sumCents, currency)}
                <span className="ml-1.5 text-[10px] text-ink-3">
                  {pct.toFixed(0)}%
                </span>
              </span>
            </div>
            <div
              aria-hidden
              className="h-1.5 overflow-hidden rounded-full bg-ink-1/[0.06]"
            >
              <div
                className={cn("h-full rounded-full bg-ando-orange/35")}
                style={{ width: `${Math.max(2, pct).toFixed(2)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
};
CategorySpendBreakdown.displayName = "CategorySpendBreakdown";
